import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const redirectTimer = useRef(null);

  useEffect(() => {
    api.get('/auth/me')
      .then(r => setUser(r.data))
      // El usuario inventado sólo sirve cuando no hay backend. Apuntando a uno
      // real (start:beta) hacía creer que había sesión, el primer /api/... daba
      // 401 y el interceptor mandaba a /login a los 3s: un rebote cada 3
      // segundos que no dejaba ni escribir el usuario.
      .catch(() => setUser(
        process.env.NODE_ENV === 'development' && !process.env.REACT_APP_API_TARGET
          ? { id: 0, name: 'Dev User', email: 'dev@local', role: 'super_admin' }
          : null
      ))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      res => res,
      err => {
        const url = err.config?.url ?? '';
        if (
          err.response?.status === 401 &&
          !url.includes('/auth/login') &&
          !url.includes('/auth/me')
        ) {
          setUser(null);
          setSessionExpired(true);
          redirectTimer.current = setTimeout(() => {
            window.location.href = '/login';
          }, 3000);
        }
        return Promise.reject(err);
      }
    );
    return () => {
      api.interceptors.response.eject(interceptor);
      clearTimeout(redirectTimer.current);
    };
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    setUser(data);
    return data;
  };

  const logout = async () => {
    await api.post('/auth/logout');
    setUser(null);
  };

  const loginWithGoogle = () => {
    window.location.href = `${window.location.origin.replace('3000', '4000')}/api/auth/google`;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, loginWithGoogle }}>
      {children}
      {sessionExpired && (
        <div style={{
          position: 'fixed',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 9999,
          background: 'var(--red-dim, #fee2e2)',
          color: 'var(--red-dark, #991b1b)',
          border: '1.5px solid var(--red, #ef4444)',
          borderRadius: 12,
          padding: '14px 28px',
          fontSize: 15,
          fontWeight: 700,
          textAlign: 'center',
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          whiteSpace: 'nowrap',
        }}>
          Tu sesión expiró. Iniciando sesión...
        </div>
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
